// ============================================================
// HoverChat Admin — Tenant Detail Page (tabbed)
// ============================================================

import { useEffect, useState } from 'react';
import { useParams, NavLink, Routes, Route } from 'react-router-dom';
import { api } from '../../lib/api';
import {
  Settings, Palette, Brain, MessageSquare, Globe,
  Database, BarChart3, ThumbsUp, Key, FileText
} from 'lucide-react';

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) {
      api.getTenant(id)
        .then(({ tenant }) => setTenant(tenant))
        .catch(console.error)
        .finally(() => setIsLoading(false));
    }
  }, [id]);

  if (isLoading) {
    return <div className="p-8"><div className="animate-pulse h-8 bg-gray-200 rounded w-48"></div></div>;
  }

  if (!tenant) {
    return <div className="p-8 text-red-600">Tenant not found</div>;
  }

  const tabs = [
    { to: '', label: 'Overview', icon: Settings, end: true },
    { to: 'widget', label: 'Widget', icon: MessageSquare },
    { to: 'theme', label: 'Theme', icon: Palette },
    { to: 'model', label: 'Model', icon: Brain },
    { to: 'prompts', label: 'Prompts', icon: FileText },
    { to: 'domains', label: 'Domains', icon: Globe },
    { to: 'knowledge', label: 'Knowledge', icon: Database },
    { to: 'conversations', label: 'Conversations', icon: MessageSquare },
    { to: 'analytics', label: 'Analytics', icon: BarChart3 },
    { to: 'feedback', label: 'Feedback', icon: ThumbsUp },
    { to: 'keys', label: 'API Keys', icon: Key },
  ];

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
        <p className="text-gray-500 mt-1">Tenant ID: {tenant.id}</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg overflow-x-auto">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`
            }
          >
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
          </NavLink>
        ))}
      </div>

      {/* Tab Content */}
      <Routes>
        <Route path="" element={<TenantOverview tenant={tenant} />} />
        <Route path="widget" element={<WidgetSettings tenantId={id!} />} />
        <Route path="theme" element={<ThemeSettings tenantId={id!} />} />
        <Route path="model" element={<ModelSettings tenantId={id!} />} />
        <Route path="prompts" element={<PromptSettings tenantId={id!} />} />
        <Route path="domains" element={<DomainSettings tenantId={id!} />} />
        <Route path="knowledge" element={<KnowledgeBase tenantId={id!} />} />
        <Route path="conversations" element={<ConversationsList tenantId={id!} />} />
        <Route path="analytics" element={<AnalyticsView tenantId={id!} />} />
        <Route path="feedback" element={<FeedbackView tenantId={id!} />} />
        <Route path="keys" element={<APIKeys tenantId={id!} />} />
      </Routes>
    </div>
  );
}

// ---- Sub-components for each tab ----

function TenantOverview({ tenant }: { tenant: any }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold mb-4">Tenant Details</h2>
      <dl className="grid grid-cols-2 gap-4">
        <div><dt className="text-sm text-gray-500">Name</dt><dd className="font-medium">{tenant.name}</dd></div>
        <div><dt className="text-sm text-gray-500">Slug</dt><dd className="font-medium">{tenant.slug}</dd></div>
        <div><dt className="text-sm text-gray-500">Status</dt><dd className="font-medium">{tenant.status}</dd></div>
        <div><dt className="text-sm text-gray-500">Created</dt><dd className="font-medium">{new Date(tenant.created_at).toLocaleString()}</dd></div>
      </dl>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Embed Code</h3>
        <code className="text-xs text-blue-800 break-all">
          {'<script src="https://chat.hovercam.com/widget.js" data-site-id="YOUR_SITE_KEY" async></script>'}
        </code>
        <p className="text-xs text-blue-700 mt-2">Find your site key in the API Keys tab.</p>
      </div>
    </div>
  );
}

function WidgetSettings({ tenantId }: { tenantId: string }) {
  const [widget, setWidget] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getWidget(tenantId).then(({ widget }) => setWidget(widget));
  }, [tenantId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateWidget(tenantId, widget);
      alert('Widget settings saved!');
    } catch (err: any) { alert(err.message); }
    finally { setSaving(false); }
  };

  if (!widget) return <div>Loading...</div>;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold mb-4">Widget Settings</h2>
      <div className="space-y-4 max-w-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Welcome Message</label>
          <textarea value={widget.welcome_message} onChange={e => setWidget({...widget, welcome_message: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} />
        </div>
        <div className="flex items-center gap-3">
          <input type="checkbox" checked={widget.enabled} onChange={e => setWidget({...widget, enabled: e.target.checked})} className="rounded" />
          <label className="text-sm">Widget Enabled</label>
        </div>
        <div className="flex items-center gap-3">
          <input type="checkbox" checked={widget.collect_leads} onChange={e => setWidget({...widget, collect_leads: e.target.checked})} className="rounded" />
          <label className="text-sm">Collect Leads</label>
        </div>
        <div className="flex items-center gap-3">
          <input type="checkbox" checked={widget.show_citations !== false} onChange={e => setWidget({...widget, show_citations: e.target.checked})} className="rounded" />
          <label className="text-sm">Show Citations</label>
          <span className="text-xs text-gray-400">Display [Source N] links below bot responses</span>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
          <select value={widget.position} onChange={e => setWidget({...widget, position: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
            <option value="bottom-right">Bottom Right</option>
            <option value="bottom-left">Bottom Left</option>
          </select>
        </div>
        <button onClick={handleSave} disabled={saving} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

function ThemeSettings({ tenantId }: { tenantId: string }) {
  const [theme, setTheme] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getTheme(tenantId).then(({ theme }) => setTheme(theme));
  }, [tenantId]);

  const handleSave = async () => {
    setSaving(true);
    try { await api.updateTheme(tenantId, theme); alert('Theme saved!'); }
    catch (err: any) { alert(err.message); }
    finally { setSaving(false); }
  };

  if (!theme) return <div>Loading...</div>;

  const colorField = (label: string, key: string) => (
    <div className="flex items-center gap-3">
      <input type="color" value={theme[key]} onChange={e => setTheme({...theme, [key]: e.target.value})} className="w-10 h-10 rounded border cursor-pointer" />
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-gray-500">{theme[key]}</p>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold mb-4">Theme Customization</h2>
      <div className="grid grid-cols-2 gap-6 max-w-2xl">
        {colorField('Primary Color', 'primary_color')}
        {colorField('Header Color', 'header_color')}
        {colorField('Background', 'background_color')}
        {colorField('Text Color', 'text_color')}
        {colorField('User Bubble', 'user_bubble_color')}
        {colorField('Bot Bubble', 'bot_bubble_color')}
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Header Title</label>
          <input type="text" value={theme.header_title} onChange={e => setTheme({...theme, header_title: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Border Radius</label>
          <input type="range" min={0} max={24} value={theme.border_radius} onChange={e => setTheme({...theme, border_radius: parseInt(e.target.value)})} className="w-full" />
          <span className="text-xs text-gray-500">{theme.border_radius}px</span>
        </div>
      </div>
      <button onClick={handleSave} disabled={saving} className="mt-6 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium">
        {saving ? 'Saving...' : 'Save Theme'}
      </button>
    </div>
  );
}

function ModelSettings({ tenantId }: { tenantId: string }) {
  const [config, setConfig] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getModelConfig(tenantId).then(({ model_config }) => setConfig(model_config));
  }, [tenantId]);

  const handleSave = async () => {
    setSaving(true);
    try { await api.updateModelConfig(tenantId, config); alert('Model config saved!'); }
    catch (err: any) { alert(err.message); }
    finally { setSaving(false); }
  };

  if (!config) return <div>Loading...</div>;

  const models = [
    { id: 'us.amazon.nova-micro-v1:0', label: 'Amazon Nova Micro (cheapest, text-only)' },
    { id: 'us.amazon.nova-lite-v1:0', label: 'Amazon Nova Lite (multimodal)' },
    { id: 'us.amazon.nova-pro-v1:0', label: 'Amazon Nova Pro (smartest)' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold mb-4">Model Configuration</h2>
      <div className="space-y-4 max-w-lg">
        <div>
          <label className="block text-sm font-medium mb-1">Default Model</label>
          <select value={config.default_model} onChange={e => setConfig({...config, default_model: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
            {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Fallback Model</label>
          <select value={config.fallback_model || ''} onChange={e => setConfig({...config, fallback_model: e.target.value || null})} className="w-full px-3 py-2 border rounded-lg text-sm">
            <option value="">None</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <input type="checkbox" checked={config.escalation_enabled} onChange={e => setConfig({...config, escalation_enabled: e.target.checked})} className="rounded" />
          <label className="text-sm">Enable Auto-Escalation</label>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Temperature: {config.temperature}</label>
          <input type="range" min={0} max={1} step={0.1} value={config.temperature} onChange={e => setConfig({...config, temperature: parseFloat(e.target.value)})} className="w-full" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Max Tokens</label>
          <input type="number" value={config.max_tokens} onChange={e => setConfig({...config, max_tokens: parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded-lg text-sm" />
        </div>

        <div className="pt-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Knowledge Base Chunking</h3>
          <p className="text-xs text-gray-500 mb-3">
            Controls how uploaded documents are split into chunks. Larger chunks preserve more context
            but require a model with a bigger context window. Changes apply to new uploads and reindexed sources.
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Chunk Size: {config.chunk_size || 500} words</label>
              <input type="range" min={100} max={3000} step={50} value={config.chunk_size || 500} onChange={e => setConfig({...config, chunk_size: parseInt(e.target.value)})} className="w-full" />
              <div className="flex justify-between text-xs text-gray-400"><span>100 (precise)</span><span>3000 (broad context)</span></div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Chunk Overlap: {config.chunk_overlap || 50} words</label>
              <input type="range" min={0} max={500} step={10} value={config.chunk_overlap || 50} onChange={e => setConfig({...config, chunk_overlap: parseInt(e.target.value)})} className="w-full" />
              <div className="flex justify-between text-xs text-gray-400"><span>0 (no overlap)</span><span>500 (heavy overlap)</span></div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Context Chunks per Query: {config.context_chunks || 5}</label>
              <input type="range" min={1} max={20} step={1} value={config.context_chunks || 5} onChange={e => setConfig({...config, context_chunks: parseInt(e.target.value)})} className="w-full" />
              <div className="flex justify-between text-xs text-gray-400"><span>1 (focused)</span><span>20 (comprehensive)</span></div>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">After changing chunk settings, reindex existing sources from the Knowledge tab for the new sizes to take effect.</p>
        </div>

        <button onClick={handleSave} disabled={saving} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium">
          {saving ? 'Saving...' : 'Save Model Config'}
        </button>
      </div>
    </div>
  );
}

function PromptSettings({ tenantId }: { tenantId: string }) {
  const [prompt, setPrompt] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.getPrompts(tenantId).then(({ prompt }) => setPrompt(prompt));
  }, [tenantId]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.updatePrompts(tenantId, prompt);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) { alert(err.message); }
    finally { setSaving(false); }
  };

  if (!prompt) return <div className="p-4">Loading...</div>;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold mb-1">System Prompt</h2>
      <p className="text-sm text-gray-500 mb-4">This is the main instruction that tells the AI how to behave. It is sent with every chat message.</p>

      <div className="space-y-5 max-w-3xl">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">System Prompt</label>
          <textarea
            value={prompt.system_prompt}
            onChange={e => setPrompt({...prompt, system_prompt: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary/20 focus:border-primary"
            rows={12}
            placeholder="You are a helpful assistant..."
          />
          <p className="text-xs text-gray-400 mt-1">{prompt.system_prompt?.length || 0} characters</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Context Injection Template</label>
          <textarea
            value={prompt.context_injection_template}
            onChange={e => setPrompt({...prompt, context_injection_template: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary/20 focus:border-primary"
            rows={5}
          />
          <p className="text-xs text-gray-500 mt-1">Use <code className="bg-gray-100 px-1 rounded">{'{context}'}</code> as the placeholder where knowledge base results will be inserted.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">No-Context Fallback Message</label>
          <textarea
            value={prompt.no_context_message}
            onChange={e => setPrompt({...prompt, no_context_message: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary/20 focus:border-primary"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">Appended when no knowledge base results are found.</p>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={handleSave} disabled={saving} className="bg-primary text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Prompts'}
          </button>
          {saved && <span className="text-green-600 text-sm">Saved successfully!</span>}
        </div>
      </div>
    </div>
  );
}

function DomainSettings({ tenantId }: { tenantId: string }) {
  const [domains, setDomains] = useState<any[]>([]);
  const [newDomain, setNewDomain] = useState('');

  const load = () => api.getDomains(tenantId).then(({ domains }) => setDomains(domains));
  useEffect(() => { load(); }, [tenantId]);

  const handleAdd = async () => {
    if (!newDomain) return;
    await api.addDomain(tenantId, newDomain);
    setNewDomain('');
    load();
  };

  const handleDelete = async (domainId: string) => {
    await api.deleteDomain(tenantId, domainId);
    load();
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold mb-4">Allowed Domains</h2>
      <p className="text-sm text-gray-500 mb-4">Only these domains can use the widget. Leave empty to allow all domains.</p>
      <div className="flex gap-2 mb-4">
        <input type="text" value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="example.com" className="flex-1 px-3 py-2 border rounded-lg text-sm" />
        <button onClick={handleAdd} className="bg-primary text-white px-4 py-2 rounded-lg text-sm">Add</button>
      </div>
      <ul className="space-y-2">
        {domains.map((d: any) => (
          <li key={d.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
            <span className="text-sm">{d.domain}</span>
            <button onClick={() => handleDelete(d.id)} className="text-red-500 text-xs hover:underline">Remove</button>
          </li>
        ))}
        {domains.length === 0 && <li className="text-sm text-gray-500">No domains configured (all domains allowed)</li>}
      </ul>
    </div>
  );
}

function KnowledgeBase({ tenantId }: { tenantId: string }) {
  const [sources, setSources] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'list' | 'upload' | 'paste' | 'search'>('list');
  const [uploading, setUploading] = useState(false);
  const [textName, setTextName] = useState('');
  const [textContent, setTextContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [message, setMessage] = useState('');

  const loadSources = async () => {
    setLoading(true);
    try {
      const { sources } = await api.getKBSources(tenantId);
      setSources(sources);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadSources(); }, [tenantId]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMessage('');
    try {
      const result = await api.uploadKBFile(tenantId, file);
      setMessage(`Uploaded "${file.name}" - ${result.chunk_count} chunks created from ${(result.text_length / 1024).toFixed(1)} KB of text`);
      loadSources();
      setTab('list');
    } catch (err: any) {
      setMessage(`Error: ${err.message}`);
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleTextSubmit = async () => {
    if (!textName.trim() || !textContent.trim()) return;
    setUploading(true);
    setMessage('');
    try {
      const result = await api.addKBText(tenantId, { name: textName, content: textContent });
      setMessage(`Added "${textName}" - ${result.chunk_count} chunks created`);
      setTextName('');
      setTextContent('');
      loadSources();
      setTab('list');
    } catch (err: any) {
      setMessage(`Error: ${err.message}`);
    }
    setUploading(false);
  };

  const handleDelete = async (sourceId: string, name: string) => {
    if (!confirm(`Delete "${name}" and all its chunks?`)) return;
    try {
      await api.deleteKBSource(tenantId, sourceId);
      setSources(sources.filter(s => s.id !== sourceId));
      setMessage(`Deleted "${name}"`);
    } catch (err: any) {
      setMessage(`Error: ${err.message}`);
    }
  };

  const handleReindex = async (sourceId: string) => {
    try {
      const result = await api.reindexKBSource(tenantId, sourceId);
      setMessage(`Reindexed - ${result.chunk_count} chunks`);
      loadSources();
    } catch (err: any) {
      setMessage(`Error: ${err.message}`);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const { results } = await api.searchKB(tenantId, searchQuery);
      setSearchResults(results);
    } catch (err: any) {
      setMessage(`Search error: ${err.message}`);
    }
    setSearching(false);
  };

  const totalChunks = sources.reduce((sum: number, s: any) => sum + (s.document_count || 0), 0);

  return (
    <div className="space-y-4">
      {/* Header stats */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">Knowledge Base</h2>
            <p className="text-sm text-gray-500">Upload text files to give the chatbot domain-specific knowledge.</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-primary">{totalChunks}</p>
            <p className="text-xs text-gray-500">total chunks</p>
          </div>
        </div>

        {/* Tab buttons */}
        <div className="flex gap-2 mb-4">
          {[
            { key: 'list', label: 'Sources' },
            { key: 'upload', label: 'Upload File' },
            { key: 'paste', label: 'Paste Text' },
            { key: 'search', label: 'Test Search' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key as any)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                tab === t.key ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Status message */}
        {message && (
          <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${message.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {message}
          </div>
        )}

        {/* Sources list */}
        {tab === 'list' && (
          loading ? <div className="text-sm text-gray-400">Loading...</div> : (
            sources.length === 0 ? (
              <div className="text-center py-8">
                <Database className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500 mb-2">No knowledge sources yet</p>
                <p className="text-xs text-gray-400">Upload a text file or paste content to get started.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {sources.map((s: any) => (
                  <li key={s.id} className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{s.name}</p>
                      <p className="text-xs text-gray-500">
                        {s.document_count} chunks
                        <span className="mx-1">&middot;</span>
                        <span className={s.status === 'indexed' ? 'text-green-600' : s.status === 'failed' ? 'text-red-600' : 'text-yellow-600'}>
                          {s.status}
                        </span>
                        {s.last_indexed_at && (
                          <>
                            <span className="mx-1">&middot;</span>
                            indexed {new Date(s.last_indexed_at).toLocaleDateString()}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button onClick={() => handleReindex(s.id)} className="text-xs text-primary hover:underline">Reindex</button>
                      <button onClick={() => handleDelete(s.id, s.name)} className="text-xs text-red-500 hover:underline">Delete</button>
                    </div>
                  </li>
                ))}
              </ul>
            )
          )
        )}

        {/* File upload */}
        {tab === 'upload' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Upload a <strong>.txt</strong>, <strong>.md</strong>, <strong>.csv</strong>, <strong>.pdf</strong>, or <strong>.docx</strong> file (max 10 MB).</p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".txt,.md,.csv,.pdf,.docx"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
                id="kb-file-input"
              />
              <label htmlFor="kb-file-input" className={`cursor-pointer ${uploading ? 'opacity-50' : ''}`}>
                <div className="text-gray-400 mb-2">
                  <svg className="h-10 w-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700">{uploading ? 'Uploading...' : 'Click to choose file'}</p>
                <p className="text-xs text-gray-400 mt-1">The file will be parsed, chunked, and indexed automatically.</p>
              </label>
            </div>
          </div>
        )}

        {/* Paste text */}
        {tab === 'paste' && (
          <div className="space-y-4 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source Name</label>
              <input
                type="text"
                value={textName}
                onChange={e => setTextName(e.target.value)}
                placeholder="e.g. Product FAQ, Company Info"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
              <textarea
                value={textContent}
                onChange={e => setTextContent(e.target.value)}
                placeholder="Paste your knowledge base content here..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary/20 focus:border-primary"
                rows={12}
              />
              <p className="text-xs text-gray-400 mt-1">{textContent.length.toLocaleString()} characters</p>
            </div>
            <button
              onClick={handleTextSubmit}
              disabled={uploading || !textName.trim() || !textContent.trim()}
              className="bg-primary text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {uploading ? 'Processing...' : 'Add to Knowledge Base'}
            </button>
          </div>
        )}

        {/* Test search */}
        {tab === 'search' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Test how the chatbot will search the knowledge base.</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Type a question to test..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              <button
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">{searchResults.length} results found:</p>
                {searchResults.map((r: any, i: number) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-primary">{r.sourceTitle}</span>
                      <span className="text-xs text-gray-400">score: {r.score?.toFixed(4)}</span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{r.content.slice(0, 400)}{r.content.length > 400 ? '...' : ''}</p>
                  </div>
                ))}
              </div>
            )}
            {searchResults.length === 0 && searchQuery && !searching && (
              <p className="text-sm text-gray-400">No results. Make sure you've added content to the knowledge base.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ConversationsList({ tenantId }: { tenantId: string }) {
  const [conversations, setConversations] = useState<any[]>([]);
  useEffect(() => { api.getConversations(tenantId).then(({ conversations }) => setConversations(conversations)); }, [tenantId]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold mb-4">Conversations</h2>
      {conversations.length === 0 ? (
        <p className="text-sm text-gray-400">No conversations yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="border-b">
            <th className="text-left py-2">ID</th><th className="text-left py-2">Messages</th><th className="text-left py-2">Model</th><th className="text-left py-2">Last Active</th>
          </tr></thead>
          <tbody>
            {conversations.map((c: any) => (
              <tr key={c.id} className="border-b hover:bg-gray-50">
                <td className="py-2 font-mono text-xs">{c.id.slice(0, 16)}...</td>
                <td className="py-2">{c.message_count}</td>
                <td className="py-2 text-xs">{c.model_used}</td>
                <td className="py-2 text-xs text-gray-500">{new Date(c.last_message_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AnalyticsView({ tenantId }: { tenantId: string }) {
  const [analytics, setAnalytics] = useState<any>(null);
  useEffect(() => { api.getAnalytics(tenantId).then(({ analytics }) => setAnalytics(analytics)); }, [tenantId]);

  if (!analytics) return <div>Loading...</div>;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold mb-4">Analytics</h2>
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 bg-blue-50 rounded-lg"><p className="text-sm text-blue-700">Conversations</p><p className="text-2xl font-bold text-blue-900">{analytics.total_conversations}</p></div>
        <div className="p-4 bg-green-50 rounded-lg"><p className="text-sm text-green-700">Messages</p><p className="text-2xl font-bold text-green-900">{analytics.total_messages}</p></div>
        <div className="p-4 bg-purple-50 rounded-lg"><p className="text-sm text-purple-700">Feedback</p><p className="text-2xl font-bold text-purple-900">{analytics.total_feedback}</p></div>
      </div>
    </div>
  );
}

function FeedbackView({ tenantId }: { tenantId: string }) {
  const [feedback, setFeedback] = useState<any[]>([]);
  useEffect(() => { api.getFeedback(tenantId).then(({ feedback }) => setFeedback(feedback)); }, [tenantId]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold mb-4">Feedback Log</h2>
      {feedback.length === 0 ? (
        <p className="text-sm text-gray-400">No feedback yet.</p>
      ) : (
        <ul className="space-y-2">
          {feedback.map((f: any) => (
            <li key={f.id} className="flex items-start gap-3 px-4 py-3 bg-gray-50 rounded-lg">
              <span className="text-lg">{f.rating === 'thumbs_up' ? '\uD83D\uDC4D' : '\uD83D\uDC4E'}</span>
              <div>
                <p className="text-sm">{f.message_content?.slice(0, 150)}...</p>
                {f.comment && <p className="text-xs text-gray-500 mt-1">"{f.comment}"</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function APIKeys({ tenantId }: { tenantId: string }) {
  const [keys, setKeys] = useState<any[]>([]);
  useEffect(() => { api.getSiteKeys(tenantId).then(({ site_keys }) => setKeys(site_keys)); }, [tenantId]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold mb-4">API Keys</h2>
      <p className="text-sm text-gray-500 mb-4">Use the public key in your embed snippet.</p>
      <ul className="space-y-2">
        {keys.map((k: any) => (
          <li key={k.id} className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium">{k.label}</p>
              <code className="text-xs text-gray-600">{k.public_key}</code>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${k.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
              {k.is_active ? 'Active' : 'Inactive'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
